import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";
import { PlayedLatencyObserver } from "../lib/played_latency";
import { applyCustomerAudioEvent } from "../lib/customer_audio_banner";

// Active call page for a single call. We join conference call_<id> so agent and customer
// share one conference. Call id comes from the page (data-active-call-id); backend
// ensures one call per dial (CreationService may reuse an existing ringing call).
// Connects to data-controller="active-call"
export default class extends Controller {
  static targets = ["timer", "transcript", "latency", "quality", "endButton", "detectedLanguage", "transferButton", "dialPad", "dialPadToggle", "dtmfDisplay", "customerAudioBanner"];
  static values = {
    id: String,
    callStarted: Number,
    summaryUrl: String,
    dtmfUrl: String,
    status: String,
    endedAt: String,
    startedAt: String,
    i18n: Object
  };

  get detectingText() {
    return this.i18nValue?.detecting || "Detecting...";
  }

  speakerLabel(speaker) {
    if (speaker === "agent") {
      return this.i18nValue?.agent || "Agent";
    }

    if (speaker === "ai") {
      return this.i18nValue?.ai_agent || "AI Agent";
    }

    return this.i18nValue?.customer || "Customer";
  }

  badgeLabel(key) {
    return this.i18nValue?.badges?.[key] || key;
  }

  connect() {
    console.warn("ActiveCall controller connected", this.idValue);

    // Reset state flags on connect
    this.callEndedProcessed = false;

    // Check if call is already completed (terminal statuses)
    const terminalStatuses = ["completed", "missed", "failed", "no_answer", "canceled"];
    const isCompleted = terminalStatuses.includes(this.statusValue) || this.endedAtValue;

    if (isCompleted) {
      // Call already ended - show final duration and redirect
      this.callEndedProcessed = true;
      this.showFinalDuration();
      setTimeout(() => {
        const fallbackUrl = window.location.pathname.replace(/\/active(\.html)?$/, "");
        const summaryUrl = this.hasSummaryUrlValue ? this.summaryUrlValue : fallbackUrl;
        window.location.href = summaryUrl;
      }, 2000);
    } else {
      // Call is still active - start timer and subscribe
      this.startTimer();
      this.subscribeToCallChannel();
      this.scrollToBottom();

      // Listen for client-side disconnect events (fallback when ActionCable broadcast fails)
      this.setupClientDisconnectListener();

      // Wait a bit for Twilio Client to initialize, then join conference
      setTimeout(() => {
        this.joinConference();
      }, 500);
    }
  }

  // Listen for Twilio/Telnyx client disconnect events
  // This provides a fallback when the ActionCable broadcast fails (e.g., webhook timeout)
  setupClientDisconnectListener() {
    this.handleClientDisconnect = this.handleClientDisconnect.bind(this);

    // Listen for both Twilio and Telnyx disconnect events
    document.addEventListener("twilio:call:disconnected", this.handleClientDisconnect);
    document.addEventListener("telnyx:call:disconnected", this.handleClientDisconnect);
  }

  // When the WebRTC call disconnects client-side, check with server for status
  async handleClientDisconnect(event) {
    console.warn("ActiveCall: Client-side disconnect detected", event.detail);

    // Skip if we already processed call_ended
    if (this.callEndedProcessed) {
      console.warn("ActiveCall: Call already processed, skipping client disconnect handler");
      return;
    }

    // Wait a moment for ActionCable broadcast to arrive (it may just be delayed)
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Check again if ActionCable delivered the call_ended event
    if (this.callEndedProcessed) {
      console.warn("ActiveCall: ActionCable delivered call_ended, skipping fallback");
      return;
    }

    // ActionCable didn't deliver - fetch status from server
    console.warn("ActiveCall: ActionCable didn't deliver call_ended, fetching from server");
    await this.fetchCallStatusFromServer();
  }

  // Fetch call status from server as fallback when ActionCable fails
  async fetchCallStatusFromServer() {
    try {
      // Extract base path from current URL (e.g., /org-slug/en/calls/123/active -> /org-slug/en/calls/123)
      const basePath = window.location.pathname.replace(/\/active(\.html)?$/, "");
      const response = await fetch(`${basePath}/status`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]")?.content || ""
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.warn("ActiveCall: Server status fetched", data);

        const terminalStatuses = ["completed", "missed", "failed", "no_answer", "canceled"];
        if (terminalStatuses.includes(data.status) || data.ended_at) {
          // Call has ended on server - trigger handleCallEnded
          this.handleCallEnded({
            status: data.status,
            ended_at: data.ended_at
          });
        }
      } else {
        console.warn("ActiveCall: Failed to fetch call status", response.status);
      }
    } catch (error) {
      console.error("ActiveCall: Error fetching call status", error);
    }
  }

  joinConference() {
    if (window.currentTwilioCall || window.currentTelnyxCall) {
      console.warn("ActiveCall: Active call already exists, skipping conference join");
      return;
    }

    const isTelnyx = this.element.getAttribute("data-controller").includes("telnyx-client");
    const clientName = isTelnyx ? "telnyx-client" : "twilio-client";
    
    let attempts = 0;
    const maxAttempts = 100;
    const conferenceName = `call_${this.idValue}`;

    console.warn(`ActiveCall: Attempting to join conference via ${clientName}`);
    
    const checkClientReady = setInterval(() => {
      attempts++;
      
      const clientController = this.getClientController();
      
      if (clientController) {
        if (clientController.device) {
          const isReady = isTelnyx ? (clientController.device.state === "connected") : (clientController.device.state === "registered");
          
          const controllerReady = typeof clientController.isReady === "function" ? clientController.isReady() : isReady;

          if (controllerReady) {
            clearInterval(checkClientReady);
            console.warn(`ActiveCall: Device ready, joining conference via ${clientName}`);
            if (typeof clientController.joinConference === "function") {
              clientController.joinConference(conferenceName).catch(e => console.error(e));
            }
            return;
          }
        }
        
        if (attempts >= 20 && !isTelnyx) {
          clearInterval(checkClientReady);
          if (typeof clientController.joinConference === "function") {
            clientController.joinConference(conferenceName).catch(e => console.error(e));
          }
          return;
        }
      }
      
      if (attempts >= maxAttempts) {
        clearInterval(checkClientReady);
        console.error(`ActiveCall: ${clientName} not found after timeout`);
      }
    }, 100);
  }

  disconnect() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.playedLatency?.stop();
    this.playedLatency = null;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Clean up client disconnect listeners
    if (this.handleClientDisconnect) {
      document.removeEventListener("twilio:call:disconnected", this.handleClientDisconnect);
      document.removeEventListener("telnyx:call:disconnected", this.handleClientDisconnect);
    }
  }

  startTimer() {
    // Use startedAt from server if available, otherwise use callStartedValue or current time
    const startTime = this.startedAtValue ? 
      new Date(this.startedAtValue).getTime() : 
      (this.callStartedValue || Date.now());
    
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      
      if (this.hasTimerTarget) {
        this.timerTarget.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }
    }, 1000);
  }

  showFinalDuration() {
    // Calculate and display final duration if call has ended
    if (this.endedAtValue && this.startedAtValue) {
      const startTime = new Date(this.startedAtValue).getTime();
      const endTime = new Date(this.endedAtValue).getTime();
      const durationSeconds = Math.floor((endTime - startTime) / 1000);
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = durationSeconds % 60;
      
      if (this.hasTimerTarget) {
        this.timerTarget.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
      }
    }
  }

  // Measures when translated audio actually starts playing here (played latency).
  // Measurement only; it does not touch what the agent hears.
  startPlayedLatency() {
    if (this.playedLatency || !this.subscription) return;
    this.playedLatency = new PlayedLatencyObserver({
      subscription: this.subscription,
      getStream: () => document.getElementById("telnyx-remote-audio")?.srcObject || null
    });
    this.playedLatency.start();
  }

  subscribeToCallChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: "CallChannel", id: this.idValue },
      {
        connected: () => {
          console.warn("Connected to CallChannel");
          this.startPlayedLatency();
        },
        
        disconnected: () => {
          console.warn("Disconnected from CallChannel");
        },
        
        received: (data) => {
          // Debug output; keep minimal
          console.warn("Received data:", data.type || data);
          
          switch(data.type) {
          case "clock_sync":
            this.playedLatency?.handleClockSync(data);
            break;
          case "playback_probe":
            this.playedLatency?.handleProbe(data);
            break;
          case "segment":
            this.handleNewSegment(data);
            break;
          case "transcript_final":
            console.warn(`[ActiveCall] transcript_final received: speaker=${data.speaker}, text_length=${data.text?.length || 0}, sequence=${data.sequence}`);
            this.handleTranscriptFinal(data);
            break;
          case "transcript_partial":
            console.warn(`[ActiveCall] transcript_partial received: speaker=${data.speaker}, text_length=${data.text?.length || 0}`);
            this.handleTranscriptPartial(data);
            break;
          case "quality_update":
            this.handleQualityUpdate(data);
            break;
          case "call_ended":
            this.handleCallEnded(data);
            break;
          case "audio":
            this.handleAudio(data);
            break;
          case "language_detected":
            this.handleLanguageDetected(data);
            break;
          case "translation":
            console.warn(`[ActiveCall] translation received: speaker=${data.speaker}, sequence=${data.sequence}`);
            this.handleTranslation(data);
            break;
          case "tts_metadata":
            this.handleTtsMetadata(data);
            break;
          case "recording_ready":
            this.handleRecordingReady(data);
            break;
          case "status_sync":
            this.handleStatusSync(data);
            break;
          case "suggestion":
            this.handleSuggestion(data);
            break;
          case "customer_audio_lost":
          case "customer_audio_restored":
            applyCustomerAudioEvent(
              this.hasCustomerAudioBannerTarget ? this.customerAudioBannerTarget : null,
              data
            );
            break;
          }
        }
      }
    );
  }

  // Remove the "Listening..." placeholder when the first transcript content arrives
  removeListeningPlaceholder() {
    if (!this.hasTranscriptTarget) return;
    const loading = this.transcriptTarget.querySelector("[data-transcript-loading]");
    if (loading) loading.remove();
  }

  handleNewSegment(data) {
    if (this.hasTranscriptTarget && data.html) {
      this.removeListeningPlaceholder();
      this.transcriptTarget.insertAdjacentHTML("beforeend", data.html);
      this.scrollToBottom();
      
      // Update latency indicator
      if (this.hasLatencyTarget && data.segment.latency_ms) {
        const latencySeconds = (data.segment.latency_ms / 1000).toFixed(1);
        this.latencyTarget.textContent = `${latencySeconds}s`;
        
        // Update color based on latency
        this.latencyTarget.classList.remove("text-green-600", "text-yellow-600", "text-red-600");
        if (data.segment.latency_ms < 2500) {
          this.latencyTarget.classList.add("text-green-600");
        } else if (data.segment.latency_ms < 4000) {
          this.latencyTarget.classList.add("text-yellow-600");
        } else {
          this.latencyTarget.classList.add("text-red-600");
        }
      }
      
      // Update detected language if available
      if (this.hasDetectedLanguageTarget && data.detected_language) {
        const currentText = this.detectedLanguageTarget.textContent.trim();
        // Only update if still detecting or if the language changed
        if (currentText === this.detectingText || currentText !== this.formatLanguageName(data.detected_language)) {
          const languageName = this.formatLanguageName(data.detected_language);
          if (languageName) {
            this.detectedLanguageTarget.textContent = languageName;
          }
        }
      } else if (this.hasDetectedLanguageTarget && 
                 data.segment.speaker === "customer" && 
                 data.segment.source_language &&
                 this.detectedLanguageTarget.textContent.trim() === this.detectingText) {
        // Fallback: update from segment if detected_language not in broadcast
        const languageName = this.formatLanguageName(data.segment.source_language);
        if (languageName) {
          this.detectedLanguageTarget.textContent = languageName;
        }
      }
    }
  }
  
  handleLanguageDetected(data) {
    if (this.hasDetectedLanguageTarget && data.language_code) {
      const languageName = this.formatLanguageName(data.language_code);
      if (languageName) {
        this.detectedLanguageTarget.textContent = languageName;
      }
    }
  }

  handleTranscriptFinal(data) {
    if (this.hasTranscriptTarget && data.text) {
      // Remove any matching partial bubble for this speaker/sequence or utterance_id
      const partialId = `partial-${data.speaker}-${data.sequence ?? "latest"}`;
      let existingPartial = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);

      // Also try to find by utterance_id if available (more reliable matching)
      if (!existingPartial && data.utterance_id) {
        existingPartial = this.transcriptTarget.querySelector(`[data-utterance-id="${data.utterance_id}"]`);
      }
      if (existingPartial) existingPartial.remove();

      if (this.hiddenTranscriptEvent(data)) return;

      this.removeListeningPlaceholder();

      // Ensure speaker is set correctly - default to customer if missing
      const speaker = data.speaker || "customer";
      const isAgent = speaker === "agent";
      const speakerClass = isAgent ? "agent" : "customer";
      const speakerLabel = this.speakerLabel(speaker);

      // Build language badges
      const sourceLang = data.language || data.source_language;
      const targetLang = data.target_language;
      const hasTranslation = data.translated_text && data.translated_text !== data.text;
      const sourceLangBadge = sourceLang ? `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-200">${this.formatLanguageName(sourceLang)}</span>` : "";
      const targetLangBadge = hasTranslation && targetLang ? `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded dark:bg-blue-700 dark:text-blue-200">${this.formatLanguageName(targetLang)}</span>` : "";

      // Build bubble content - include final translation if available
      const bubbleContent = hasTranslation ? this.buildFinalBubbleWithTranslation(data, isAgent) : `<p class="text-sm leading-relaxed">${this.linkifyText(this.escapeHtml(data.text))}</p>`;

      const html = `
        <div class="transcript-entry ${speakerClass}" data-sequence="${data.sequence ?? ""}" data-speaker="${speaker}" data-utterance-id="${data.utterance_id || ""}">
          <div class="flex gap-2 items-start ${isAgent ? "justify-end" : "justify-start"}">
            <div class="flex-1 max-w-3xl">
              <div class="mb-1">
                <span class="text-xs font-semibold text-gray-600 dark:text-gray-300">${speakerLabel}</span>
                ${sourceLangBadge ? `<span class="ml-2">${sourceLangBadge}</span>` : ""}
                ${targetLangBadge ? `<span class="ml-2">${targetLangBadge}</span>` : ""}
              </div>
              <div class="${isAgent ? "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-900/60 dark:text-blue-100 dark:border-blue-800" : "bg-green-100 text-green-900 border-green-200 dark:bg-emerald-900/60 dark:text-emerald-100 dark:border-emerald-800"} border rounded-2xl px-4 py-3 shadow-sm">
                ${bubbleContent}
              </div>
              <div class="latency-container text-[10px] ${isAgent ? "text-right justify-end" : "text-left justify-start"} mt-1 flex flex-wrap gap-2">
                ${this.buildPhaseLatencyBadges(data)}
              </div>
            </div>
          </div>
        </div>
      `;

      this.transcriptTarget.insertAdjacentHTML("beforeend", html);
      this.scrollToBottom();
    }
  }

  // Build bubble content with original text and final translation
  buildFinalBubbleWithTranslation(data, isAgent) {
    const originalText = data.text || "";
    const translatedText = data.translated_text || "";
    const textColorClass = isAgent ? "text-blue-900 dark:text-blue-100" : "text-green-900 dark:text-emerald-100";
    const borderColorClass = isAgent ? "border-blue-200 dark:border-blue-700" : "border-green-200 dark:border-green-700";
    const originalLabel = this.i18nValue?.original_label || "Original:";
    const translatedLabel = this.i18nValue?.translated_label || "Translated:";

    return `
      <div class="mb-2">
        <div class="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">${originalLabel}</div>
        <p class="text-sm leading-relaxed ${textColorClass}">${this.linkifyText(this.escapeHtml(originalText))}</p>
      </div>
      <div class="pt-2 border-t ${borderColorClass}">
        <div class="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">${translatedLabel}</div>
        <p class="text-sm leading-relaxed ${textColorClass}">${this.linkifyText(this.escapeHtml(translatedText))}</p>
      </div>
    `;
  }

  handleTranscriptPartial(data) {
    if (this.hasTranscriptTarget && data.text) {
      if (this.hiddenTranscriptEvent(data)) return;

      this.removeListeningPlaceholder();
      // Ensure speaker is set correctly - default to customer if missing
      const speaker = data.speaker || "customer";
      const partialId = `partial-${speaker}-${data.sequence ?? "latest"}`;
      let partialElement = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);

      // Check for translation preview from Go runtime
      const hasTranslationPreview = data.translation_preview === true && data.translated_text;

      if (!partialElement) {
        const isAgent = speaker === "agent";
        const speakerClass = isAgent ? "agent" : "customer";
        const speakerLabel = this.speakerLabel(speaker);
        const sourceLang = data.language || data.source_language;
        const targetLang = data.translation_language;
        const sourceLangBadge = sourceLang ? `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-200">${this.formatLanguageName(sourceLang)}</span>` : "";
        const targetLangBadge = hasTranslationPreview && targetLang ? `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-indigo-800 bg-indigo-100 rounded dark:bg-indigo-700 dark:text-indigo-200">${this.formatLanguageName(targetLang)}</span>` : "";

        // Build bubble content - include translation preview if available
        const bubbleContent = hasTranslationPreview ? this.buildPartialBubbleWithTranslation(data, isAgent) : `<p class="text-sm leading-relaxed">${this.linkifyText(this.escapeHtml(data.text))}</p>`;

        const html = `
          <div class="transcript-entry ${speakerClass} opacity-80" data-partial-id="${partialId}" data-speaker="${speaker}" data-utterance-id="${data.utterance_id || ""}" ${(data.sequence !== undefined && data.sequence !== null) ? `data-sequence="${data.sequence}"` : ""}>
            <div class="flex gap-2 items-start ${isAgent ? "justify-end" : "justify-start"}">
              <div class="flex-1 max-w-3xl">
                <div class="mb-1">
                  <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">${speakerLabel}</span>
                  ${sourceLangBadge ? `<span class="ml-2">${sourceLangBadge}</span>` : ""}
                  ${targetLangBadge ? `<span class="ml-2">${targetLangBadge}</span>` : ""}
                </div>
                <div class="partial-bubble ${isAgent ? "bg-blue-50 text-blue-900 border-blue-100 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-800/70" : "bg-emerald-50 text-emerald-900 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-800/70"} border rounded-2xl px-4 py-3">
                  ${bubbleContent}
                </div>
                <div class="latency-container text-[10px] ${isAgent ? "text-right justify-end" : "text-left justify-start"} mt-1 flex flex-wrap gap-2">
                  ${this.buildPhaseLatencyBadges(data)}
                </div>
              </div>
            </div>
          </div>
        `;

        this.transcriptTarget.insertAdjacentHTML("beforeend", html);
        partialElement = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);
      } else {
        // Update existing partial entry
        const bubble = partialElement.querySelector(".partial-bubble");
        if (bubble) {
          if (hasTranslationPreview) {
            const isAgent = speaker === "agent";
            bubble.innerHTML = this.buildPartialBubbleWithTranslation(data, isAgent);

            // Also update language badges in header if translation language changed
            const header = partialElement.querySelector("div > div > div.mb-1");
            if (header && data.translation_language) {
              const targetLangBadge = `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-indigo-800 bg-indigo-100 rounded dark:bg-indigo-700 dark:text-indigo-200">${this.formatLanguageName(data.translation_language)}</span>`;
              // Only add if not already present
              if (!header.querySelector(".bg-indigo-100")) {
                header.insertAdjacentHTML("beforeend", `<span class="ml-2">${targetLangBadge}</span>`);
              }
            }
          } else {
            // Just update the text
            const textElement = bubble.querySelector("p");
            if (textElement) {
              textElement.innerHTML = this.linkifyText(this.escapeHtml(data.text));
            }
          }
        }
      }

      this.scrollToBottom();
    }
  }

  // Build bubble content with original text and translation preview
  buildPartialBubbleWithTranslation(data, isAgent) {
    const originalText = data.text || "";
    const translatedText = data.translated_text || "";
    const textColorClass = isAgent ? "text-blue-900 dark:text-blue-100" : "text-emerald-900 dark:text-emerald-100";
    const borderColorClass = isAgent ? "border-blue-200/50 dark:border-blue-700/50" : "border-emerald-200/50 dark:border-emerald-700/50";
    const previewLabel = this.i18nValue?.translation_preview_label || "Translation preview:";

    return `
      <div class="mb-2">
        <p class="text-sm leading-relaxed ${textColorClass}">${this.linkifyText(this.escapeHtml(originalText))}</p>
      </div>
      <div class="pt-2 border-t ${borderColorClass}">
        <div class="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 italic">${previewLabel}</div>
        <p class="text-sm leading-relaxed ${textColorClass} opacity-75 italic">${this.linkifyText(this.escapeHtml(translatedText))}</p>
      </div>
    `;
  }

  hiddenTranscriptEvent(data) {
    const metadata = data.metadata || {};
    const text = (data.text || "").trim().toLowerCase();

    if (data.speaker === "system") return true;
    if (data.transcript_type === "system_announcement") return true;
    if (metadata.type === "system_announcement") return true;
    if (metadata.include_in_customer_transcript === false) return true;

    return [
      /^this call (is being|may be)?\s*recorded\b/,
      /this call is being recorded for quality assurance purposes/,
      /\bquality assurance purposes\b/,
      /^esta llamada\b.*\b(siendo|grabada)\b/,
      /\bpor llamar\b.*\besta llamada\b.*\b(siendo|grabada)\b/,
      /\bgracias por llamar\b.*\besta llamada\b.*\b(siendo|grabada)\b/,
      /^conectando (ahora|ya)\.?$/,
      /^connecting (you )?now\.?$/
    ].some((pattern) => pattern.test(text));
  }

  // A turn can be split into sentences that share its sequence; each sentence's
  // utterance_id picks the right entry, the sequence covers older events.
  transcriptEntryFor(data, speaker) {
    if (data.utterance_id) {
      const entry = this.transcriptTarget.querySelector(`.transcript-entry[data-utterance-id="${CSS.escape(data.utterance_id)}"]`);
      if (entry) return entry;
    }
    return this.transcriptTarget.querySelector(`[data-sequence="${data.sequence}"][data-speaker="${speaker}"]`);
  }

  handleTranslation(data) {
    if (!this.hasTranscriptTarget || data.sequence === undefined || data.sequence === null) return;
    
    // Find the transcript entry by sequence and speaker
    // Check both final entries (data-sequence) and partial entries (data-partial-id)
    const speaker = data.speaker || "customer";
    let entry = this.transcriptEntryFor(data, speaker);
    
    // If not found as final entry, try to find as partial entry
    if (!entry) {
      const partialId = `partial-${speaker}-${data.sequence}`;
      entry = this.transcriptTarget.querySelector(`[data-partial-id="${partialId}"]`);
    }
    
    if (!entry) {
      console.warn(`[ActiveCall] Translation received but transcript entry not found: sequence=${data.sequence}, speaker=${speaker}`);
      return;
    }
    
    // Get the bubble container (the rounded div with background color)
    const bubble = entry.querySelector(".rounded-2xl");
    if (!bubble) return;
    
    const isAgent = speaker === "agent";
    const sourceLang = data.source_language;
    const targetLang = data.target_language;
    const originalText = data.original_text || "";
    const translatedText = data.translated_text || "";
    
    // Build language badges
    const sourceLangBadge = sourceLang ? `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded dark:bg-gray-700 dark:text-gray-200">${this.formatLanguageName(sourceLang)}</span>` : "";
    const targetLangBadge = targetLang ? `<span class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded dark:bg-blue-700 dark:text-blue-200">${this.formatLanguageName(targetLang)}</span>` : "";
    
    // Update the header with language badges
    const header = entry.querySelector("div > div > div.mb-1");
    if (header) {
      const speakerLabel = this.speakerLabel(speaker);
      header.innerHTML = `
        <span class="text-xs font-semibold text-gray-600 dark:text-gray-300">${speakerLabel}</span>
        ${sourceLangBadge ? `<span class="ml-2">${sourceLangBadge}</span>` : ""}
        ${targetLangBadge ? `<span class="ml-2">${targetLangBadge}</span>` : ""}
      `;
    }
    
    // Update the bubble content with original and translated text
    const textColorClass = isAgent ? "text-blue-900 dark:text-blue-100" : "text-green-900 dark:text-emerald-100";
    if (translatedText && translatedText !== originalText) {
      bubble.innerHTML = `
        <div class="mb-2">
          <div class="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">${this.i18nValue?.original_label || "Original:"}</div>
          <p class="text-sm leading-relaxed ${textColorClass}">${this.linkifyText(this.escapeHtml(originalText))}</p>
        </div>
        <div class="pt-2 border-t ${isAgent ? "border-blue-200 dark:border-blue-700" : "border-green-200 dark:border-green-700"}">
          <div class="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">${this.i18nValue?.translated_label || "Translated:"}</div>
          <p class="text-sm leading-relaxed ${textColorClass}">${this.linkifyText(this.escapeHtml(translatedText))}</p>
        </div>
      `;
    } else {
      // Just show original text if no translation or same as original
      bubble.innerHTML = `<p class="text-sm leading-relaxed ${textColorClass}">${this.linkifyText(this.escapeHtml(originalText))}</p>`;
    }
    
    // Add translation latency if available
    if (data.translation_latency_ms) {
      const latencyContainer = entry.querySelector(".latency-container");
      if (latencyContainer) {
        latencyContainer.insertAdjacentHTML("beforeend", this.buildLatencyBadge("Trans", data.translation_latency_ms));
      }
    }

    this.scrollToBottom();
  }

  handleTtsMetadata(data) {
    if (!this.hasTranscriptTarget || data.sequence === undefined || data.sequence === null) return;
    const speaker = data.speaker || "customer";
    const entry = this.transcriptEntryFor(data, speaker);
    if (!entry) return;

    if (data.tts_latency_ms) {
      const latencyContainer = entry.querySelector(".latency-container");
      if (latencyContainer) {
        latencyContainer.insertAdjacentHTML("beforeend", this.buildLatencyBadge("TTS", data.tts_latency_ms));
      }
    }
  }

  buildPhaseLatencyBadges(data) {
    const latency = data.latency || {};
    const badges = [];
    const sttMs = data.stt_latency_ms || latency.stt_final_latency_ms || latency.stt_partial_latency_ms;
    const translationMs = data.translation_latency_ms || latency.translation_latency_ms;
    const ttsMs = data.tts_latency_ms || latency.tts_latency_ms;
    const playbackRequestedMs = data.speech_end_to_playback_requested_ms || latency.speech_end_to_playback_requested_ms;
    const turnMs = data.turn_latency_ms || latency.turn_latency_ms;
    const turnObservation = data.turn_latency_observation || latency.turn_latency_observation;

    if (sttMs) badges.push(this.buildLatencyBadge("STT", sttMs));
    if (translationMs) badges.push(this.buildLatencyBadge("Trans", translationMs));
    if (ttsMs) badges.push(this.buildLatencyBadge("TTS", ttsMs));
    if (playbackRequestedMs) badges.push(this.buildPlaybackRequestedBadge(playbackRequestedMs));
    if (turnMs) badges.push(this.buildTurnLatencyBadge(turnMs, turnObservation));

    return badges.join("");
  }

  buildPlaybackRequestedBadge(latencyMs) {
    const latencySecs = (latencyMs / 1000).toFixed(2);
    const label = this.badgeLabel("playback_requested");
    const title = this.badgeLabel("playback_requested_title");
    return `<span class="inline-flex items-center px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-700" title="${title}">${label} ${latencySecs}s</span>`;
  }

  buildTurnLatencyBadge(latencyMs, observation) {
    const latencySecs = (latencyMs / 1000).toFixed(2);
    const confirmed = observation === "provider_confirmed";
    const colorClass = confirmed
      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
      : "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800";
    const suffix = confirmed ? this.badgeLabel("confirmed") : this.badgeLabel("requested");
    const turnLabel = this.badgeLabel("turn");
    const title = this.badgeLabel("turn_title");
    return `<span class="inline-flex items-center px-1.5 py-0.5 rounded border ${colorClass}" title="${title}">${turnLabel} ${latencySecs}s ${suffix}</span>`;
  }

  buildLatencyBadge(type, latencyMs) {
    const latencySecs = (latencyMs / 1000).toFixed(2);
    if (type === "STT") {
      const isGood = (latencyMs / 1000) < 2.5;
      const colorClass = isGood ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800" : "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800";
      const title = this.badgeLabel("stt_title");
      const label = this.badgeLabel("stt");
      return `<span class="inline-flex items-center px-1.5 py-0.5 rounded border ${colorClass}" title="${title}"><svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path></svg>${label} ${latencySecs}s</span>`;
    } else if (type === "Trans") {
      const title = this.badgeLabel("translation_title");
      const label = this.badgeLabel("translation");
      return `<span class="inline-flex items-center px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800" title="${title}"><svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path></svg>${label} ${latencySecs}s</span>`;
    } else if (type === "TTS") {
      const title = this.badgeLabel("tts_title");
      const label = this.badgeLabel("tts");
      return `<span class="inline-flex items-center px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800" title="${title}"><svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"></path></svg>${label} ${latencySecs}s</span>`;
    }
    return "";
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  // Linkify text - makes phone numbers, emails, and URLs clickable
  // Must be called AFTER escapeHtml to avoid XSS
  linkifyText(escapedText) {
    if (!escapedText) return escapedText;

    // Phone pattern - matches international formats
    // +1 234 567 8900, +44 20 7123 4567, (123) 456-7890, etc.
    const phonePattern = /(?:\+\d{1,4}[\s.-]?)?(?:\(?\d{1,4}\)?[\s.-]?)(?:\d{1,4}[\s.-]?){1,3}\d{1,4}/g;

    // Email pattern
    const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

    // URL pattern - matches http, https, and www URLs
    const urlPattern = /(?:https?:\/\/|www\.)[^\s<>[\]]+(?<![.,;:!?\]}])/gi;

    const linkClass = "text-indigo-600 dark:text-indigo-400 hover:underline font-medium";

    // Replace URLs first (before email, as URLs might contain @)
    let result = escapedText.replace(urlPattern, (url) => {
      const href = url.startsWith("http") ? url : `https://${url}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="${linkClass}">${url}</a>`;
    });

    // Replace emails
    result = result.replace(emailPattern, (email) => {
      return `<a href="mailto:${email}" class="${linkClass}">${email}</a>`;
    });

    // Replace phone numbers (at least 7 digits to avoid false positives)
    result = result.replace(phonePattern, (phone) => {
      const digitsOnly = phone.replace(/\D/g, "");
      if (digitsOnly.length < 7) return phone; // Skip if too short

      // Format for tel: link
      const telHref = phone.includes("+") ? phone.replace(/[^\d+]/g, "") : digitsOnly;
      return `<a href="tel:${telHref}" class="${linkClass}">${phone}</a>`;
    });

    return result;
  }

  handleRecordingReady(data) {
    // If we're on the active call page, we can show a notification
    // The recording will be available on the call show page after redirect
    // For now, just log it - the call show page will refresh when user navigates there
    if (data.recording_url) {
      console.warn(`ActiveCall: Recording is ready: ${data.recording_url}`);
      // Optionally show a notification or update UI
      // The recording card on the call show page will auto-refresh via Turbo Frame
    }
  }

  formatLanguageName(langCode) {
    // Map of common language codes to full names (matches application_helper.rb)
    const languageMap = {
      "en": "English",
      "es": "Spanish",
      "fr": "French",
      "de": "German",
      "it": "Italian",
      "pt": "Portuguese",
      "ru": "Russian",
      "pl": "Polish",
      "nl": "Dutch",
      "sv": "Swedish",
      "da": "Danish",
      "no": "Norwegian",
      "fi": "Finnish",
      "cs": "Czech",
      "hu": "Hungarian",
      "ro": "Romanian",
      "bg": "Bulgarian",
      "hr": "Croatian",
      "sk": "Slovak",
      "sl": "Slovenian",
      "et": "Estonian",
      "lv": "Latvian",
      "lt": "Lithuanian",
      "el": "Greek",
      "tr": "Turkish",
      "ar": "Arabic",
      "zh": "Chinese",
      "ja": "Japanese",
      "ko": "Korean",
      "hi": "Hindi"
    };
    
    if (!langCode) return null;
    
    // Extract base language code (e.g., "de" from "de-CH")
    const baseCode = langCode.toLowerCase().split("-")[0];
    return languageMap[baseCode] || baseCode.toUpperCase();
  }

  handleQualityUpdate(data) {
    if (this.hasQualityTarget) {
      this.qualityTarget.textContent = data.quality;
    }
    
    if (this.hasLatencyTarget) {
      const latencySeconds = (data.latency / 1000).toFixed(1);
      this.latencyTarget.textContent = `${latencySeconds}s`;
    }
  }

  handleStatusSync(data) {
    console.warn("ActiveCall: Status sync received", data);

    // Update data attributes to reflect current server state
    if (data.status) {
      this.statusValue = data.status;
    }
    if (data.ended_at) {
      this.endedAtValue = data.ended_at;
    }
    if (data.started_at) {
      this.startedAtValue = data.started_at;
    }

    // If status indicates call is already ended, the server will also send call_ended
    // No need to handle here - let handleCallEnded do its job
  }

  handleCallEnded(data) {
    console.warn("ActiveCall: Call ended event received", data);

    // Prevent duplicate processing (e.g., from reconnection + original broadcast)
    if (this.callEndedProcessed) {
      console.warn("ActiveCall: Call ended already processed, skipping duplicate");
      return;
    }
    this.callEndedProcessed = true;

    // Update data attributes to reflect ended state
    if (data.status) {
      this.statusValue = data.status;
    }
    if (data.ended_at) {
      this.endedAtValue = data.ended_at;
    }

    // Stop the timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Disable the end call button to prevent further clicks
    if (this.hasEndButtonTarget) {
      this.endButtonTarget.disabled = true;
      this.endButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }

    // Hang up the Twilio/Telnyx call if still active
    const clientController = this.getClientController();

    if (clientController && clientController.currentCall) {
      console.warn("Hanging up client call");
      try {
        if (typeof clientController.currentCall.disconnect === "function") {
          clientController.currentCall.disconnect();
        } else if (typeof clientController.currentCall.hangup === "function") {
          clientController.currentCall.hangup();
        }
      } catch (e) {
        console.warn("Error hanging up call:", e);
      }
      clientController.currentCall = null;
      window.currentTwilioCall = null;
      window.currentTelnyxCall = null;
    }

    // Show a non-intrusive notification overlay instead of alert
    this.showCallEndedNotification(data.status);

    // Redirect to call summary after a delay
    setTimeout(() => {
      const fallbackUrl = window.location.pathname.replace(/\/active(\.html)?$/, "");
      const summaryUrl = this.hasSummaryUrlValue ? this.summaryUrlValue : fallbackUrl;
      window.location.href = summaryUrl;
    }, 2000);
  }

  showCallEndedNotification(status) {
    // Create a notification banner at the top of the page
    const notification = document.createElement("div");
    notification.className = "realtime-notification flex fixed top-4 left-1/2 z-50 items-center px-6 py-3 space-x-3 transform -translate-x-1/2 animate-fade-in";
    notification.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
      </svg>
      <span class="font-medium">
        ${status === "completed" ? "Call ended" : status === "missed" ? "Call missed" : "Call " + status}
      </span>
      <span class="text-sm opacity-75">Redirecting to summary...</span>
    `;

    document.body.appendChild(notification);

  }

  handleSuggestion(data) {
    if (!data.suggestion) return;
    
    console.warn("ActiveCall: Agent assist suggestion received", data.suggestion);
    
    // Create a suggestion toast notification for the agent
    const notification = document.createElement("div");
    
    // Determine colors based on severity
    let bgColor = "bg-blue-50 dark:bg-blue-900/30";
    let iconColor = "text-blue-500 dark:text-blue-400";
    let textColor = "text-blue-800 dark:text-blue-200";
    
    if (data.suggestion.severity === "high" || data.suggestion.severity === "critical") {
      bgColor = "bg-red-50 dark:bg-red-900/30";
      iconColor = "text-red-500 dark:text-red-400";
      textColor = "text-red-800 dark:text-red-200";
    } else if (data.suggestion.severity === "medium") {
      bgColor = "bg-yellow-50 dark:bg-yellow-900/30";
      iconColor = "text-yellow-500 dark:text-yellow-400";
      textColor = "text-yellow-800 dark:text-yellow-200";
    }
    
    notification.className = `realtime-notification fixed bottom-24 right-6 z-50 p-4 max-w-sm ${bgColor} animate-fade-in transition-all duration-300 transform translate-y-0 opacity-100 flex items-start space-x-3`;
    
    notification.innerHTML = `
      <div class="flex-shrink-0 ${iconColor}">
        <svg class="w-5 h-5 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold ${textColor}">${this.escapeHtml(data.suggestion.title)}</p>
        <p class="mt-1 text-sm ${textColor} opacity-90">${this.escapeHtml(data.suggestion.content)}</p>
      </div>
      <button class="flex-shrink-0 text-gray-400 hover:text-gray-500 focus:outline-none transition-colors" onclick="this.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove after 15 seconds
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.classList.remove("translate-y-0", "opacity-100");
        notification.classList.add("translate-y-2", "opacity-0");
        setTimeout(() => notification.remove(), 300);
      }
    }, 15000);
  }

  scrollToBottom() {
    if (this.hasTranscriptTarget) {
      this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight;
    }
  }

  endCall() {
    console.warn("ActiveCall: endCall called");
    
    // Don't prevent default - let the form submit normally after hanging up
    // The turbo_confirm dialog will handle confirmation
    
    // Hang up the Twilio/Telnyx call first
    const clientController = this.getClientController();
    
    // Try to hang up via controller
    if (clientController && typeof clientController.hangup === "function") {
      console.warn("ActiveCall: Hanging up call via controller");
      clientController.hangup();
    } else if (clientController && typeof clientController.hangUp === "function") {
      console.warn("ActiveCall: Hanging up call via controller");
      clientController.hangUp();
    } else if (window.currentTwilioCall) {
      // Fallback: try window.currentTwilioCall
      console.warn("ActiveCall: Using window.currentTwilioCall fallback");
      try {
        if (typeof window.currentTwilioCall.disconnect === "function") {
          window.currentTwilioCall.disconnect();
        } else if (typeof window.currentTwilioCall.hangup === "function") {
          window.currentTwilioCall.hangup();
        }
        window.currentTwilioCall = null;
      } catch (error) {
        console.error("ActiveCall: Error hanging up call:", error);
      }
    } else if (window.currentTelnyxCall) {
      try {
        window.currentTelnyxCall.hangup();
        window.currentTelnyxCall = null;
      } catch (error) {
        console.error("ActiveCall: Error hanging up Telnyx call:", error);
      }
    } else {
      console.warn("ActiveCall: No call found to hang up");
    }
    
    // The form submission will handle the server-side end call
    // Don't prevent default - let the form submit normally
  }

  toggleMute(event) {
    console.warn("ActiveCall: toggleMute called");
    event.preventDefault();
    event.stopPropagation();
    
    // Find the twilio/telnyx client controller
    const clientController = this.getClientController();
    
    if (clientController && typeof clientController.toggleMute === "function") {
      const isMuted = clientController.toggleMute();
      console.warn("ActiveCall: Mute toggled, isMuted:", isMuted);
      const button = event.currentTarget;
      const icon = button.querySelector("svg");
      
      // Update button appearance based on actual mute state
      if (isMuted) {
        button.classList.add("bg-red-500", "text-white");
        button.classList.remove("text-gray-600", "dark:text-gray-400");
        button.title = "Unmute";
        // Change icon to muted microphone
        if (icon) {
          icon.innerHTML = "<path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z\" /><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2\" />";
        }
      } else {
        button.classList.remove("bg-red-500", "text-white");
        button.classList.add("text-gray-600", "dark:text-gray-400");
        button.title = "Mute";
        // Change icon back to unmuted microphone
        if (icon) {
          icon.innerHTML = "<path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z\" />";
        }
      }
    } else {
      console.error("ActiveCall: Twilio Client controller or toggleMute method not found", clientController);
    }
  }

  createTicket(event) {
    console.warn("ActiveCall: createTicket called");
    event.preventDefault();
    event.stopPropagation();

    const callId = this.idValue;
    const localePrefix = this.localePrefix();
    const ticketUrl = `${localePrefix}/tickets/new?call_id=${callId}`;

    let modalFrame = document.getElementById("ticket_modal");
    if (!modalFrame) {
      modalFrame = document.createElement("turbo-frame");
      modalFrame.id = "ticket_modal";
      document.body.appendChild(modalFrame);
    }

    // Clear previous content to force reload even if URL is same
    modalFrame.removeAttribute("src");
    modalFrame.innerHTML = "";
    modalFrame.src = ticketUrl;
  }

  addNote(event) {
    console.warn("ActiveCall: addNote called");
    event.preventDefault();
    event.stopPropagation();
    
    // Open the note modal using Turbo Frame
    const callId = this.idValue;
    const localePrefix = this.localePrefix();
    const modalUrl = `${localePrefix}/calls/${callId}/notes/new`;
    
    // Create or get the turbo frame for the modal
    let modalFrame = document.getElementById("note_modal_frame");
    if (!modalFrame) {
      modalFrame = document.createElement("turbo-frame");
      modalFrame.id = "note_modal_frame";
      document.body.appendChild(modalFrame);
    }
    
    // Load the modal content
    modalFrame.src = modalUrl;
  }

  emailCustomer(event) {
    console.warn("ActiveCall: emailCustomer called");
    event.preventDefault();
    event.stopPropagation();

    const callId = this.idValue;
    const localePrefix = this.localePrefix();
    const emailUrl = `${localePrefix}/calls/${callId}/emails/new`;

    let modalFrame = document.getElementById("email_modal_frame");
    if (!modalFrame) {
      modalFrame = document.createElement("turbo-frame");
      modalFrame.id = "email_modal_frame";
      document.body.appendChild(modalFrame);
    }

    // Clear previous content to force reload even if URL is same
    modalFrame.removeAttribute("src");
    modalFrame.innerHTML = "";
    modalFrame.src = emailUrl;
  }

  adjustVolume(event) {
    console.warn("ActiveCall: adjustVolume called");
    const volume = event.target.value / 100;
    const clientController = this.getClientController();
    
    if (clientController && typeof clientController.setVolume === "function") {
      clientController.setVolume(volume);
    } else if (clientController && clientController.currentCall) {
      // Try to set volume on the call object directly
      try {
        if (typeof clientController.currentCall.setVolume === "function") {
          clientController.currentCall.setVolume(volume);
        }
      } catch (error) {
        console.error("ActiveCall: Error adjusting volume:", error);
      }
    } else {
      console.warn("ActiveCall: Could not adjust volume - controller or method not found");
    }
  }

  localePrefix() {
    const lang = document.documentElement.getAttribute("lang");
    if (lang && lang.length > 0) {
      return `/${lang}`;
    }
    return "";
  }

  // Audio Playback & Dubbing Logic
  
  handleAudio(data) {
    if (!data.audio) return;
    
    console.warn("ActiveCall: Received audio segment", data.transcript_id);
    
    // Initialize audio queue if needed
    if (!this.audioQueue) {
      this.audioQueue = [];
      this.isPlayingAudio = false;
    }
    
    // Add to queue
    this.audioQueue.push(data);
    
    // Start playback if not already playing
    if (!this.isPlayingAudio) {
      this.playNextAudio();
    }
  }
  
  async playNextAudio() {
    if (!this.audioQueue || this.audioQueue.length === 0) {
      this.isPlayingAudio = false;
      this.restoreCallVolume();
      return;
    }

    this.isPlayingAudio = true;
    const data = this.audioQueue.shift();

    try {
      // Lower call volume (Dubbing effect)
      this.lowerCallVolume();

      // Decode base64 audio
      const audioData = this.base64ToArrayBuffer(data.audio);

      // Initialize AudioContext if needed (must be after user interaction)
      if (!this.audioContext) {
        try {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          // Set output device to match Twilio call (for AirPods/headsets)
          await this.syncAudioContextOutput();
        } catch (err) {
          console.error("ActiveCall: AudioContext not supported:", err);
          // Skip audio playback if AudioContext is not available
          this.playNextAudio();
          return;
        }
      }

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      // Decode and play with timeout
      const decodePromise = this.audioContext.decodeAudioData(audioData);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Audio decode timeout")), 5000)
      );

      const buffer = await Promise.race([decodePromise, timeoutPromise]);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);

      source.onended = () => {
        // Play next segment
        this.playNextAudio();
      };

      source.onerror = (error) => {
        console.error("ActiveCall: Audio playback error:", error);
        // Continue to next audio on error
        this.playNextAudio();
      };

      source.start(0);

    } catch (error) {
      console.error("ActiveCall: Error playing audio:", error);
      // Skip to next on error
      this.playNextAudio();
    }
  }

  // Sync AudioContext output device with Twilio call device (for AirPods/headsets)
  async syncAudioContextOutput() {
    if (!this.audioContext) return;

    try {
      // Get the selected speaker from Twilio controller
      const clientController = this.getClientController();
      const speakerId = clientController?.selectedSpeakerId;

      if (speakerId && typeof this.audioContext.setSinkId === "function") {
        await this.audioContext.setSinkId(speakerId);
        console.warn("ActiveCall: AudioContext output synced to:", speakerId);
      } else if (!speakerId) {
        // Fallback: try to get default speaker
        const devices = await navigator.mediaDevices.enumerateDevices();
        const defaultSpeaker = devices.find(d => d.kind === "audiooutput" && d.deviceId === "default");
        if (defaultSpeaker && typeof this.audioContext.setSinkId === "function") {
          await this.audioContext.setSinkId(defaultSpeaker.deviceId);
          console.warn("ActiveCall: AudioContext output set to default speaker");
        }
      }
    } catch (error) {
      // setSinkId may not be supported in all browsers
      console.warn("ActiveCall: Could not sync AudioContext output device:", error);
    }
  }
  
  base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
  
  lowerCallVolume() {
    if (this.isDubbingActive) return;
    
    console.warn("ActiveCall: Lowering call volume (Dubbing)");
    this.isDubbingActive = true;
    
    const clientController = this.getClientController();
    if (clientController) {
      // Store original volume if possible, or assume 1.0
      // Set volume to 10%
      if (typeof clientController.setVolume === "function") {
        clientController.setVolume(0.1);
      } else if (clientController.currentCall && typeof clientController.currentCall.setVolume === "function") {
        clientController.currentCall.setVolume(0.1);
      }
    }
  }
  
  restoreCallVolume() {
    if (!this.isDubbingActive) return;
    
    console.warn("ActiveCall: Restoring call volume");
    this.isDubbingActive = false;
    
    const clientController = this.getClientController();
    if (clientController) {
      // Restore to 100% (or user preference if we tracked it)
      if (typeof clientController.setVolume === "function") {
        clientController.setVolume(1.0);
      } else if (clientController.currentCall && typeof clientController.currentCall.setVolume === "function") {
        clientController.currentCall.setVolume(1.0);
      }
    }
  }
  
  getClientController() {
    try {
      const isTelnyx = this.element.getAttribute("data-controller").includes("telnyx-client");
      const clientName = isTelnyx ? "telnyx-client" : "twilio-client";
      
      // Try same element
      let controller = this.application.getControllerForElementAndIdentifier(this.element, clientName);
      if (controller) return controller;

      // Try document query
      const el = document.querySelector(`[data-controller*='${clientName}']`);
      if (el) {
        return this.application.getControllerForElementAndIdentifier(el, clientName);
      }
      
      // Try window fallbacks
      const globalDevice = isTelnyx ? window.telnyxDevice : window.twilioDevice;
      if (globalDevice) {
        const allElements = document.querySelectorAll("[data-controller]");
        for (const iter of allElements) {
          const controllers = iter.getAttribute("data-controller").split(" ");
          if (controllers.includes(clientName)) {
            try {
              controller = this.application.getControllerForElementAndIdentifier(iter, clientName);
              if (controller) return controller;
            } catch {
              // ignore
            }
          }
        }
      }
    } catch (e) {
      console.warn("ActiveCall: Error getting client controller:", e);
    }
    return null;
  }

  // Transfer call from AI agent to human agent
  async transferToMe(event) {
    console.warn("ActiveCall: transferToMe called");
    event.preventDefault();
    event.stopPropagation();

    const callId = this.idValue;
    if (!callId) {
      console.error("ActiveCall: No call ID available for transfer");
      return;
    }

    // Disable button while processing
    if (this.hasTransferButtonTarget) {
      this.transferButtonTarget.disabled = true;
      this.transferButtonTarget.textContent = "Transferring...";
    }

    try {
      // Call the takeover endpoint
      const localePrefix = this.localePrefix();
      const response = await fetch(`${localePrefix}/calls/${callId}/takeover`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        console.warn("ActiveCall: Takeover successful", data);

        // Update button to show success
        if (this.hasTransferButtonTarget) {
          this.transferButtonTarget.textContent = "Connected!";
          this.transferButtonTarget.classList.remove("bg-purple-600", "hover:bg-purple-700");
          this.transferButtonTarget.classList.add("bg-green-600");
        }

        // Join the conference as the human agent
        this.joinConference();

        // Hide the AI agent card after a short delay
        setTimeout(() => {
          if (this.hasTransferButtonTarget) {
            this.transferButtonTarget.closest(".bg-purple-50, .dark\\:bg-purple-900\\/20")?.remove();
          }
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("ActiveCall: Takeover failed", response.status, errorData);

        // Re-enable button
        if (this.hasTransferButtonTarget) {
          this.transferButtonTarget.disabled = false;
          this.transferButtonTarget.textContent = "Transfer to Me";
        }

        // Show error message
        this.showFlashMessage(errorData.error || "Failed to transfer call", "error");
      }
    } catch (error) {
      console.error("ActiveCall: Error during takeover", error);

      // Re-enable button
      if (this.hasTransferButtonTarget) {
        this.transferButtonTarget.disabled = false;
        this.transferButtonTarget.textContent = "Transfer to Me";
      }

      this.showFlashMessage("Network error during transfer", "error");
    }
  }

  showFlashMessage(message, type = "info") {
    const flashContainer = document.getElementById("flash");
    if (flashContainer) {
      const flashDiv = document.createElement("div");
      const variant = type === "error" ? "error" : "info";
      flashDiv.className = `app-toast app-toast--${variant}`;
      flashDiv.setAttribute("role", type === "error" ? "alert" : "status");
      flashDiv.setAttribute("aria-live", type === "error" ? "assertive" : "polite");
      flashDiv.innerHTML = `
        <div class="app-toast__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 8v5m0 3.5v.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path></svg>
        </div>
        <div class="app-toast__content"><div class="app-toast__message"></div></div>
      `;
      flashDiv.querySelector(".app-toast__message").textContent = message;
      flashContainer.appendChild(flashDiv);

      setTimeout(() => {
        flashDiv.classList.add("dismissible--closing");
        setTimeout(() => flashDiv.remove(), 300);
      }, 5000);
    }
  }

  // DTMF Dial Pad Methods

  toggleDialPad(event) {
    event.preventDefault();
    if (this.hasDialPadTarget) {
      this.dialPadTarget.classList.toggle("hidden");
      // Rotate the toggle arrow
      if (this.hasDialPadToggleTarget) {
        const svg = this.dialPadToggleTarget.querySelector("svg");
        if (svg) {
          svg.classList.toggle("rotate-180");
        }
      }
    }
  }

  async sendDtmf(event) {
    event.preventDefault();
    const digit = event.currentTarget.dataset.digit;
    if (!digit) return;

    console.warn("ActiveCall: sendDtmf called with digit:", digit);

    // Update display
    if (this.hasDtmfDisplayTarget) {
      this.dtmfDisplayTarget.textContent += digit;
    }

    // Play local tone feedback (optional audio feedback)
    this.playDtmfTone(digit);

    const clientController = this.getClientController();
    if (clientController && typeof clientController.sendDigit === "function") {
      const sent = clientController.sendDigit(digit);
      if (sent) {
        console.warn("ActiveCall: DTMF sent through active browser call:", digit);
        return;
      }
    }

    // Fall back to server-side Telnyx Call Control when no active browser call is available.
    try {
      const response = await fetch(this.dtmfUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]")?.content || ""
        },
        body: JSON.stringify({ digits: digit })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.error("ActiveCall: Failed to send DTMF", response.status, data);
        this.showFlashMessage(data.error || "Failed to send tone", "error");
      } else {
        console.warn("ActiveCall: DTMF sent successfully:", digit);
      }
    } catch (error) {
      console.error("ActiveCall: Error sending DTMF", error);
      this.showFlashMessage("Network error sending tone", "error");
    }
  }

  clearDtmfDisplay(event) {
    event.preventDefault();
    if (this.hasDtmfDisplayTarget) {
      this.dtmfDisplayTarget.textContent = "";
    }
  }

  // Play a short DTMF tone locally for feedback
  playDtmfTone(digit) {
    // DTMF frequencies: each digit is a combination of two frequencies
    const dtmfFrequencies = {
      "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
      "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
      "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
      "*": [941, 1209], "0": [941, 1336], "#": [941, 1477]
    };

    const frequencies = dtmfFrequencies[digit];
    if (!frequencies) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const duration = 0.15; // 150ms tone

      // Create two oscillators for the dual-tone
      frequencies.forEach(freq => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);

        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Low volume
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      });
    } catch (error) {
      // Audio feedback is optional, don't fail if it doesn't work
      console.warn("ActiveCall: Could not play DTMF tone:", error);
    }
  }
}
